import { IcoArrowUpRight16 } from '@onefootprint/icons';
import type { AgentOutputMapping, AiDecisionNodeConfig, ApiNodeDataAiDecision } from '@onefootprint/request-types/dashboard';
import { Divider, Form, LinkButton } from '@onefootprint/ui';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import { agentBuilderUrl } from '@/config/constants';
import useAgent from '@/hooks/use-agent';
import useAgentReleases from '@/hooks/use-agent-releases';
import useAvailableAgents from '@/hooks/use-available-agents';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../../base-node';
import type { AiDecisionFormData } from '../../types';
import { INVESTIGATION_ID_OUTPUT_OPTION_VALUE } from '../../types';
import { getDefaultOutputs } from '../../utils/get-default-outputs';
import { alignInputsToDeclared, parseDecisionInputsToInputs, parseInputsToDecisionInputs } from '../../utils/parse-inputs-to-decision-inputs';
import InputData from '../input-data';
import OutputData from '../output-data';
import WhatWillPercyDo from '../what-will-percy-do/what-will-percy-do';
type AiDecisionNodeFormProps = {
  node: NodeProps<ApiNodeDataAiDecision>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
export const AiDecisionNodeForm = ({
  node,
  mutation,
  canEdit
}: AiDecisionNodeFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'aiDecisionNode'
  });
  const nodeConfigContext = useGetNodeConfigContext(node.id, 'templatable');
  const {
    toggle
  } = useDirty();
  const {
    data: {
      config
    }
  } = node;
  const form = useForm<AiDecisionFormData>({
    disabled: !canEdit || undefined,
    defaultValues: {
      name: node.data.name || t($ => $.nodeName.fallback),
      inputs: parseInputsToDecisionInputs(node.data.inputs),
      customObjective: config?.kind === 'custom' ? config.objective : undefined,
      customOutputSchema: config?.kind === 'custom' ? JSON.stringify(config.outputSchema) : undefined,
      // Empty string when unbound so the agent dropdown shows its placeholder.
      agentId: config?.kind === 'agent' ? config.agentId : '',
      agentVersionId: config?.kind === 'agent' ? config.agentVersionId : undefined,
      outputs: config?.kind === 'agent' ? config.outputs.map(output => ({
        fieldKey: output.source === 'investigation_id' ? INVESTIGATION_ID_OUTPUT_OPTION_VALUE : output.fieldKey,
        onboardingDataKey: output.onboardingDataKey
      })) : []
    }
  });
  const {
    handleSubmit
  } = form;
  useEffect(() => {
    toggle('ai-decision-form', form.formState.isDirty);
  }, [form.formState.isDirty]);
  const autocompleteItems = nodeConfigContext?.autocomplete;
  const languageDefinition = nodeConfigContext?.languageDefinition;
  const getConfig = (data: AiDecisionFormData): AiDecisionNodeConfig | undefined => {
    if (config?.kind === 'custom') {
      const outputSchema = JSON.parse(data.customOutputSchema ?? '{}');
      return {
        kind: 'custom',
        objective: data.customObjective ?? '',
        outputSchema
      };
    }
    // config is null, 'agent', or a legacy/unmodeled kind: without an agent + version bound the
    // node stays unbound (null config), and a legacy config has nothing here to persist.
    if (!data.agentId || !data.agentVersionId) return undefined;
    const outputs: AgentOutputMapping[] = data.outputs.map(output_0 => output_0.fieldKey === INVESTIGATION_ID_OUTPUT_OPTION_VALUE ? {
      source: 'investigation_id',
      onboardingDataKey: output_0.onboardingDataKey
    } : {
      source: 'agent_output',
      fieldKey: output_0.fieldKey,
      onboardingDataKey: output_0.onboardingDataKey
    });
    return {
      kind: 'agent',
      agentId: data.agentId,
      agentVersionId: data.agentVersionId,
      outputs
    };
  };
  const handleFormSubmit = async (data_0: AiDecisionFormData) => {
    const resolvedConfig = getConfig(data_0);
    // An ai_decision node update requires a config. An unbound node (no agent/custom chosen
    // yet) has nothing to persist, so skip the save until a binding is selected.
    if (!resolvedConfig) return;
    try {
      await mutation.mutateAsync({
        nodeId: node.id,
        kind: 'update_node',
        nodeData: {
          kind: 'ai_decision',
          name: data_0.name,
          inputs: parseDecisionInputsToInputs(data_0.inputs, selectedRelease?.inputs ?? []),
          config: resolvedConfig
        }
      });
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };

  // The agent + version selectors render when the node is unbound (null config) or already bound to
  // an agent. They key off the form's selected agent so the version list reacts before save.
  const isAgentOrUnbound = !config || config.kind === 'agent';
  const selectedAgentId = useWatch({
    control: form.control,
    name: 'agentId'
  });
  // Empty string (the placeholder state) means no agent is bound yet.
  const {
    data: agentDetail
  } = useAgent(selectedAgentId);
  const agents = useAvailableAgents();
  // The dedicated fetch is authoritative (and works for an unpublished/off-page agent), but falls
  // back to the list so a just-picked agent's name/link are available before it resolves.
  const agent = agentDetail ?? agents.items.find(a => a.id === selectedAgentId);
  const releases = useAgentReleases(selectedAgentId, !!selectedAgentId);
  // Agent: objective, output schema, and input keys are derived from the config; only
  // Custom exposes them as editable form fields.
  const isManagedConfig = config?.kind !== 'custom';

  // Apply a chosen version: set the field, merge its declared inputs with any existing values, and
  // sync the node name.
  const handleReleaseChange = (agentVersionId: string) => {
    form.setValue('agentVersionId', agentVersionId, {
      shouldDirty: true
    });
    const release_0 = releases.items.find(release => release.id === agentVersionId);
    if (!release_0) return;
    const existingInputByKey = new Map(form.getValues('inputs').map(input => [input.key, input]));
    const merged = release_0.inputs.map(input_0 => ({
      ...(existingInputByKey.get(input_0.fieldName) ?? {
        key: input_0.fieldName,
        expression: '',
        comment: input_0.comment
      }),
      key: input_0.fieldName
    }));
    form.setValue('inputs', merged, {
      shouldDirty: true
    });
    // Keep the author's existing output mappings whose field key still exists on the chosen version.
    // If none survive (nothing configured yet, or the agent was swapped wholesale so no prior field
    // keys exist), seed a sensible default set from the version's "headline" outputs.
    const availableOutputKeys = new Set(release_0.outputs);
    const survivingOutputs = form.getValues('outputs').filter(output_1 => output_1.fieldKey === INVESTIGATION_ID_OUTPUT_OPTION_VALUE || availableOutputKeys.has(output_1.fieldKey));
    const nextOutputs = survivingOutputs.length > 0 ? survivingOutputs : getDefaultOutputs(release_0.outputs);
    form.setValue('outputs', nextOutputs, {
      shouldDirty: true
    });
    if (agent) {
      const versionLabel = release_0.versionTag ? ` (v${release_0.versionTag})` : '';
      form.setValue('name', `${agent.name}${versionLabel}`, {
        shouldDirty: true
      });
    }
  };

  // Tracks the agent the current version belongs to, so we only auto-select a new version when the
  // user actually switches agents — not when the same agent's releases reload or paginate, and not
  // on initial mount of an already-bound node (which keeps the form clean until edited).
  const versionAgentIdRef = useRef(config?.kind === 'agent' ? config.agentId : undefined);
  useEffect(() => {
    if (!selectedAgentId || releases.isLoading || releases.items.length === 0) return;
    if (versionAgentIdRef.current === selectedAgentId) return;
    versionAgentIdRef.current = selectedAgentId;
    handleReleaseChange(releases.items[0].id);
  }, [selectedAgentId, releases.items, releases.isLoading]);
  const releaseOptions = releases.items.map(release_1 => ({
    value: release_1.id,
    label: `v${release_1.versionTag ?? release_1.id}`
  }));
  const publishedAgents = agents.items.filter(a_0 => !!a_0.latestRelease);
  // Always keep the bound agent selectable, even if it isn't on the first page or is unpublished.
  const selectableAgents = agent && !publishedAgents.some(a_1 => a_1.id === agent.id) ? [agent, ...publishedAgents] : publishedAgents;
  const selectedAgentVersionId = useWatch({
    control: form.control,
    name: 'agentVersionId'
  });
  const selectedRelease = releases.items.find(release_2 => release_2.id === selectedAgentVersionId);
  const availableOutputs = selectedRelease?.outputs ?? [];

  // shouldDirty stays false: a materialized blank row shows what storage already means, not an edit.
  useEffect(() => {
    if (!selectedRelease) return;
    const aligned = alignInputsToDeclared(form.getValues('inputs'), selectedRelease.inputs);
    form.setValue('inputs', aligned, {
      shouldDirty: false
    });
  }, [selectedRelease?.id]);
  return <FormProvider {...form}>
      <form id="ai-decision-form" className="flex flex-col gap-6" onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col gap-4">
          {isAgentOrUnbound && <div className="flex flex-col gap-3 border border-tertiary rounded-default p-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-label-2 text-primary">{t($_0 => $_0.agentVersion.title)}</div>
                  {selectedAgentId && <LinkButton href={agentBuilderUrl(selectedAgentId, {
                versionId: selectedAgentVersionId
              })} target="_blank" iconComponent={IcoArrowUpRight16} iconPosition="right">
                      {t($_1 => $_1.agentVersion.viewAgent)}
                    </LinkButton>}
                </div>
                <Form.Select<AiDecisionFormData> name="agentId" aria-label={t($_2 => $_2.agentVersion.agentSelectLabel)} placeholder={t($_3 => $_3.agentVersion.selectAgentPlaceholder)} options={selectableAgents.map(a_2 => ({
              value: a_2.id,
              label: a_2.name
            }))} disabled={!canEdit || mutation.isPending || agents.isLoading} size="compact" onOpenChange={open => {
              if (open && agents.hasNextPage && !agents.isFetchingNextPage) agents.fetchNextPage();
            }} />
                {selectedAgentId && (releaseOptions.length === 0 && !releases.isLoading ? <div className="text-body-2 text-tertiary">{t($_4 => $_4.agentVersion.noPublishedVersion)}</div> : <Form.Select<AiDecisionFormData> name="agentVersionId" aria-label={t($_5 => $_5.agentVersion.selectLabel)} onChange={handleReleaseChange} options={releaseOptions} disabled={!canEdit || mutation.isPending || releases.isLoading} size="compact" onOpenChange={open_0 => {
              if (open_0 && releases.hasNextPage && !releases.isFetchingNextPage) releases.fetchNextPage();
            }} />)}
              </div>
            </div>}
          <div className="flex flex-col gap-2 w-full md:flex-row md:justify-between md:items-center md:gap-0">
            <div className="text-label-2">{t($_6 => $_6.nodeName.label)}</div>
            <div className="w-full md:w-[50%]">
              <Form.Input<AiDecisionFormData> name="name" disabled={!canEdit || !!selectedAgentId} className="md:w-[50%]" size="compact" />
            </div>
          </div>
          <Divider />
          {config && <WhatWillPercyDo config={config} />}
          <InputData canEdit={canEdit} autocompleteItems={autocompleteItems} languageDefinition={languageDefinition} agentManaged={isManagedConfig} declaredInputs={selectedRelease?.inputs} vaultDocumentAccess={selectedRelease ? {
          enabled: selectedRelease.includeAllVaultDocuments,
          available: selectedRelease.canIncludeAllVaultDocuments
        } : undefined} />
          {isAgentOrUnbound && !!selectedAgentVersionId && <OutputData canEdit={canEdit} availableOutputs={availableOutputs} />}
          {config?.kind === 'custom' && <>
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1">
                  <div className="text-label-2 text-primary">{t($_7 => $_7.objective.label)}</div>
                  <div className="text-body-2 text-tertiary">{t($_8 => $_8.objective.description)}</div>
                </div>
                <Form.MarkdownEditor<AiDecisionFormData> disabled={!canEdit} name="customObjective" placeholder={t($_9 => $_9.objective.placeholder)} />
              </div>
              <Form.SchemaEditor<AiDecisionFormData> name="customOutputSchema" disabled={!canEdit} rules={{
            required: t($_10 => $_10.outputSchema.required)
          }} title={t($_11 => $_11.outputSchema.title)} subtitle={t($_12 => $_12.outputSchema.subtitle)} />
            </>}
        </div>
      </form>
    </FormProvider>;
};
export default AiDecisionNodeForm;